import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import {
  CellClassParams,
  ColDef,
  GridOptions,
  ProcessCellForExportParams,
  ProcessDataFromClipboardParams,
  RowClassParams,
} from 'ag-grid-community';
import {
  createClipboardCellParser,
  gridEditingKeyboardHandler,
  trimTrailingEmptyClipboardRow,
} from '../../../../../ui/grid-clipboard/grid-clipboard.utils';
import { CampaignMeasureStore } from '../../../../../../stores/campaign-measure.store';
import { PermissionFacadeService } from '../../../../../../services/permission-facade.service';
import { CampaignMeasureDetailed, CampaignStatus } from '../../../../../../types/campaign.interface';
import { MeasureValueStatus } from '../../../../../../types/measure.interface';
import { MeasureColumnVisibility } from '../../../../../measures/measure-column-toggle/measure-column-toggle.component';
import {
  MeasureStatusBadgeComponent,
  STATUS_LABEL_KEYS,
} from '../../../../../measures/measure-status-badge/measure-status-badge.component';
import { EXCEL_SET_FILTER_PARAMS, formatCellForClipboard, noRowsOverlay } from '../../../../../../utils/grid/grid-presets';
import { CampaignPageStore } from './campaign-page.store';
import { MeasureNameCellComponent } from './measure-name-cell.component';
import { NumericCellEditorComponent } from '../../../../../ui/grid-editors/numeric-cell-editor.component';

export interface MeasureGridRow {
  id: string;
  measureName: string;
  measureTypeName: string;
  obligatoire: boolean;
  localisation: string;
  parameterName: string;
  parameterValue: number | null;
  unitInput: string;
  unitCalculated: string;
  calculatedValue: number | null;
  valueMinChecker: number | null;
  valueMaxChecker: number | null;
  inputDataId?: number;
  formulaInputId: number;
  campaignId: number;
  measureId: number;
  campaignMeasureId: number;
  originalValue: number | null;
  status: MeasureValueStatus | undefined;
  isInactive: boolean;
}

export interface PendingModification {
  rowId: string;
  inputDataId?: number;
  formulaInputId: number;
  campaignId: number;
  measureId: number;
  value: number | null;
  originalValue: number | null;
  type: 'update' | 'create';
}

const formatDecimal = (value: unknown): string => {
  return typeof value === 'number' && Number.isFinite(value) ? value.toFixed(2) : '-';
};

// Clipboard mirrors the on-screen blanks: cells rendered as '-' (nullish values or the
// baked placeholder in the row data) copy as empty cells, matching the Tableaux grids.
const processCampaignCellForClipboard = (params: ProcessCellForExportParams): string => {
  const formatted = formatCellForClipboard(params);
  return formatted === '-' ? '' : formatted;
};

@Injectable({
  providedIn: 'root',
})
export class CampaignMeasuresGridService {
  private static readonly PERMISSION_PATH = 'monitoring/campaigns/:id/measures';
  private static readonly MEASURE_CONFIG_PERMISSION_PATH = 'configuration/measures';

  private readonly pageStore = inject(CampaignPageStore);
  private readonly campaignMeasureStore = inject(CampaignMeasureStore);
  private readonly permissionFacade = inject(PermissionFacadeService);
  private readonly translate = inject(TranslocoService);

  readonly pendingModifications = signal<Map<string, PendingModification>>(new Map());
  readonly highlightedRowIds = signal<Set<string>>(new Set());
  readonly newRowIds = signal<Set<string>>(new Set());
  /** measureIds whose rows show the transient "Calcul en cours" badge while ces-calcul
   *  recomputes them (fed by the enqueue 'pending' broadcast); the status valueGetter reads it. */
  readonly computingMeasureIds = signal<ReadonlySet<number>>(new Set());

  constructor() {
    effect(() => {
      const campaignId = this.pageStore.campaignId();
      untracked(() => {
        // Switching to a different campaign drops any stale Pending overlay. (Same-campaign tab
        // switches are handled by the measures component clearing on mount/destroy — the effect
        // does not re-fire when campaignId is unchanged.)
        this.clearComputing();
        this.campaignMeasureStore.setHasPendingChanges(this.hasPendingForCampaign(campaignId));
      });
    });
  }

  markComputing(measureIds: readonly number[]): void {
    if (!measureIds.length) return;
    this.computingMeasureIds.update(prev => {
      const next = new Set(prev);
      measureIds.forEach(id => next.add(id));
      return next;
    });
  }

  clearComputing(): void {
    if (this.computingMeasureIds().size) this.computingMeasureIds.set(new Set());
  }

  private hasPendingForCampaign(campaignId: number | null, modifications?: Map<string, PendingModification>): boolean {
    if (!campaignId) return false;
    const mods = modifications ?? this.pendingModifications();
    return Array.from(mods.values()).some(m => m.campaignId === campaignId);
  }

  readonly hasWriteAccess = this.permissionFacade.hasWriteAccess(CampaignMeasuresGridService.PERMISSION_PATH);
  readonly hasMeasureReadAccess = this.permissionFacade.hasReadAccess(CampaignMeasuresGridService.MEASURE_CONFIG_PERMISSION_PATH);

  readonly isReadOnly = computed(() => {
    const campaign = this.pageStore.currentCampaign();
    const campaignDone = campaign?.status === CampaignStatus.DONE;
    return campaignDone || !this.hasWriteAccess();
  });

  readonly hasModifications = computed(() => this.hasPendingForCampaign(this.pageStore.campaignId()));

  readonly detailedMeasures = computed(() => this.campaignMeasureStore.detailedMeasures());

  readonly gridRowData = computed<MeasureGridRow[]>(() => {
    const measures = this.detailedMeasures();
    if (!measures.length) return [];

    const rows: MeasureGridRow[] = [];

    measures.forEach((cm: CampaignMeasureDetailed) => {
      const measureName = cm.measureName || '-';
      const measureTypeName = cm.measureTypeName || '-';
      const localisation = cm.localisationName ?? '-';
      const unitCalculated = cm.unitSymbol ?? '-';
      const calculatedValue = cm.value ?? null;
      const valueMinChecker = cm.valueMinChecker ?? null;
      const valueMaxChecker = cm.valueMaxChecker ?? null;
      const isInactive = cm.hasValidFormula === false;

      if (!cm.inputData || cm.inputData.length === 0) {
        rows.push({
          id: `${cm.id}-0`,
          measureName,
          measureTypeName,
          obligatoire: cm.measurePriority ?? false,
          localisation,
          parameterName: '-',
          parameterValue: null,
          unitInput: '-',
          unitCalculated,
          calculatedValue,
          valueMinChecker,
          valueMaxChecker,
          formulaInputId: 0,
          campaignId: cm.campaignId,
          measureId: cm.measureId,
          campaignMeasureId: cm.id,
          originalValue: null,
          status: cm.status,
          isInactive,
        });
      } else {
        cm.inputData.forEach(input => {
          rows.push({
            id: `${cm.id}-${input.formulaInputId}`,
            measureName,
            measureTypeName,
            obligatoire: cm.measurePriority ?? false,
            localisation,
            parameterName: input.parameterName || '-',
            parameterValue: input.value,
            unitInput: input.parameterUnitSymbol || '-',
            unitCalculated,
            calculatedValue,
            valueMinChecker,
            valueMaxChecker,
            inputDataId: input.id,
            formulaInputId: input.formulaInputId,
            campaignId: cm.campaignId,
            measureId: cm.measureId,
            campaignMeasureId: cm.id,
            originalValue: input.value,
            status: cm.status,
            isInactive,
          });
        });
      }
    });

    const pending = this.pendingModifications();
    if (pending.size === 0) return rows;

    return rows.map(row => {
      const mod = pending.get(row.id);
      return mod ? { ...row, parameterValue: mod.value } : row;
    });
  });

  // AG Grid 34 silently disables `colDef.spanRows` whenever `cellSelection` is on
  // (see ag-grid-community validator). We need cellSelection for paste-from-Excel,
  // so we recover the "same measure" visual grouping by hiding duplicate cell
  // contents in CSS while keeping the row layout intact.
  private readonly isMergedDuplicate = (params: CellClassParams<MeasureGridRow>): boolean => {
    const rowIndex = params.node.rowIndex;
    if (rowIndex === null || rowIndex <= 0) return false;
    const previous = params.api.getDisplayedRowAtIndex(rowIndex - 1);
    return previous?.data?.campaignMeasureId !== undefined && previous.data.campaignMeasureId === params.data?.campaignMeasureId;
  };

  private readonly mergedMeasureCellRule: Record<string, (params: CellClassParams<MeasureGridRow>) => boolean> = {
    'cell-merged-duplicate': this.isMergedDuplicate,
  };

  // A measure with several inputs spans consecutive rows sharing campaignMeasureId.
  // The last row of each group carries the separator that delimits one measure from
  // the next; rows inside a group stay borderless so they read as a single block.
  // The final group is closed too (the grid has no outer border), so the table ends
  // on a clean rule rather than trailing off.
  private readonly isMeasureGroupEnd = (params: RowClassParams<MeasureGridRow>): boolean => {
    const rowIndex = params.node.rowIndex;
    if (rowIndex === null) return false;
    const next = params.api.getDisplayedRowAtIndex(rowIndex + 1);
    if (!next?.data) return true;
    return next.data.campaignMeasureId !== params.data?.campaignMeasureId;
  };

  getRowClass(params: RowClassParams<MeasureGridRow>): string {
    const data = params.data;
    if (!data) return '';
    const classes: string[] = [];
    if (this.newRowIds().has(data.id)) classes.push('row-remote-updated');
    if (data.isInactive) classes.push('row-inactive');
    if (this.isMeasureGroupEnd(params)) classes.push('row-measure-end');
    return classes.join(' ');
  }

  createColumnDefs(visibility: MeasureColumnVisibility): ColDef<MeasureGridRow>[] {
    const cols: ColDef<MeasureGridRow>[] = [
      {
        field: 'measureName',
        headerName: this.translate.translate('entities.campaign.grid.measure'),
        minWidth: 200,
        flex: 1.5,
        filter: 'agSetColumnFilter',
        filterParams: EXCEL_SET_FILTER_PARAMS,
        cellClassRules: this.mergedMeasureCellRule,
        cellRenderer: MeasureNameCellComponent,
      },
      {
        field: 'measureTypeName',
        headerName: this.translate.translate('entities.campaign.grid.measureType'),
        minWidth: 160,
        flex: 1,
        filter: 'agSetColumnFilter',
        filterParams: EXCEL_SET_FILTER_PARAMS,
        cellClassRules: this.mergedMeasureCellRule,
      },
      {
        field: 'obligatoire',
        headerName: this.translate.translate('entities.campaign.grid.mandatory'),
        width: 110,
        filter: 'agSetColumnFilter',
        filterParams: {
          ...EXCEL_SET_FILTER_PARAMS,
          valueFormatter: (params: { value: boolean | null }) =>
            this.translate.translate(params.value ? 'common.buttons.yes' : 'common.buttons.no'),
        },
        valueFormatter: (params: { value: boolean | null }) =>
          this.translate.translate(params.value ? 'common.buttons.yes' : 'common.buttons.no'),
        cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
        cellClassRules: this.mergedMeasureCellRule,
        cellRenderer: (params: { value: boolean }) =>
          `<i class="pi ${params.value ? 'pi-check-circle' : 'pi-times-circle'}" style="font-size:1.25rem;color:${params.value ? '#22c55e' : '#94a3b8'};"></i>`,
      },
      {
        field: 'localisation',
        headerName: this.translate.translate('entities.campaign.grid.location'),
        minWidth: 120,
        flex: 1,
        filter: 'agSetColumnFilter',
        filterParams: EXCEL_SET_FILTER_PARAMS,
        cellClassRules: this.mergedMeasureCellRule,
      },
      {
        field: 'parameterName',
        headerName: this.translate.translate('entities.campaign.grid.parameter'),
        minWidth: 150,
        flex: 1,
      },
      {
        field: 'parameterValue',
        headerName: this.translate.translate('entities.campaign.grid.valueInput'),
        headerClass: 'header-editable',
        width: 120,
        editable: params => !this.isReadOnly() && params.data?.formulaInputId !== 0 && !params.data?.isInactive,
        cellEditor: NumericCellEditorComponent,
        valueFormatter: params => {
          if (params.value === null || params.value === undefined) return '';
          return String(params.value);
        },
        cellClassRules: {
          'cell-editable-input': params => !this.isReadOnly() && params.data?.formulaInputId !== 0 && !params.data?.isInactive,
          'cell-modified': params => {
            const rowId = params.data?.id;
            return rowId ? this.pendingModifications().has(rowId) : false;
          },
          'cell-remote-updated': params => {
            const rowId = params.data?.id;
            return rowId ? this.highlightedRowIds().has(rowId) : false;
          },
        },
      },
      {
        field: 'unitInput',
        headerName: this.translate.translate('entities.campaign.grid.unitInput'),
        width: 90,
      },
    ];

    if (visibility.status) {
      cols.push({
        colId: 'status',
        headerName: this.translate.translate('entities.campaign.grid.status'),
        width: 130,
        filter: 'agSetColumnFilter',
        filterParams: {
          ...EXCEL_SET_FILTER_PARAMS,
          values: [
            MeasureValueStatus.Empty,
            MeasureValueStatus.Error,
            MeasureValueStatus.OutOfBounds,
            MeasureValueStatus.Unchecked,
            MeasureValueStatus.Valid,
            MeasureValueStatus.Pending,
          ],
          valueFormatter: (params: { value: MeasureValueStatus | null }) => this.statusLabel(params.value),
        },
        cellClassRules: this.mergedMeasureCellRule,
        // While ces-calcul recomputes the row (enqueue 'pending' broadcast), surface the
        // transient "Calcul en cours" (Pending) badge; the settle refetch restores the real status.
        valueGetter: params => {
          const row = params.data;
          if (!row) return undefined;
          return this.computingMeasureIds().has(row.measureId) ? MeasureValueStatus.Pending : row.status;
        },
        // Column-level formatter drives clipboard/export; the set filter ignores it when
        // `values` are provided, so the same formatter is repeated in filterParams.
        valueFormatter: (params: { value: MeasureValueStatus | null | undefined }) => this.statusLabel(params.value),
        cellRenderer: MeasureStatusBadgeComponent,
      });
    }

    if (visibility.calculated) {
      cols.push(
        {
          field: 'calculatedValue',
          headerName: this.translate.translate('entities.campaign.grid.calculatedValue'),
          minWidth: 140,
          flex: 1,
          cellClassRules: this.mergedMeasureCellRule,
          valueFormatter: params => formatDecimal(params.value),
          cellClass: params => {
            const rowId = params.data?.id;
            return rowId && this.highlightedRowIds().has(rowId) ? 'cell-remote-updated' : '';
          },
        },
        {
          field: 'unitCalculated',
          headerName: this.translate.translate('entities.campaign.grid.unitCalculated'),
          width: 90,
          cellClassRules: this.mergedMeasureCellRule,
        },
      );
    }

    if (visibility.verification) {
      cols.push(
        {
          field: 'valueMinChecker',
          headerName: this.translate.translate('entities.campaign.grid.verificationMin'),
          width: 150,
          cellClassRules: this.mergedMeasureCellRule,
          valueFormatter: params => formatDecimal(params.value),
        },
        {
          field: 'valueMaxChecker',
          headerName: this.translate.translate('entities.campaign.grid.verificationMax'),
          width: 150,
          cellClassRules: this.mergedMeasureCellRule,
          valueFormatter: params => formatDecimal(params.value),
        },
      );
    }

    return cols;
  }

  createDefaultColDef(): ColDef {
    return {
      sortable: false,
      resizable: true,
      suppressKeyboardEvent: gridEditingKeyboardHandler,
    };
  }

  createGridOptions(): GridOptions<MeasureGridRow> {
    return {
      theme: 'legacy',
      stopEditingWhenCellsLoseFocus: true,
      singleClickEdit: true,
      getRowId: params => params.data.id,
      animateRows: false,
      cellSelection: { suppressMultiRanges: false },
      suppressLastEmptyLineOnPaste: true,
      processCellForClipboard: processCampaignCellForClipboard,
      processDataFromClipboard: this.processDataFromClipboard.bind(this),
      processCellFromClipboard: this.processCellFromClipboard,
      overlayNoRowsTemplate: noRowsOverlay(this.translate.translate('common.messages.noData')),
      getRowClass: this.getRowClass.bind(this),
    };
  }

  addModification(mod: PendingModification): void {
    const current = this.pendingModifications();
    const updated = new Map(current);
    updated.set(mod.rowId, mod);
    this.pendingModifications.set(updated);
    this.campaignMeasureStore.setHasPendingChanges(true);
  }

  removeModification(rowId: string): void {
    const current = this.pendingModifications();
    if (current.has(rowId)) {
      const updated = new Map(current);
      updated.delete(rowId);
      this.pendingModifications.set(updated);
      this.campaignMeasureStore.setHasPendingChanges(this.hasPendingForCampaign(this.pageStore.campaignId(), updated));
    }
  }

  clearAllModifications(): void {
    const campaignId = this.pageStore.campaignId();
    if (!campaignId) {
      this.pendingModifications.set(new Map());
    } else {
      const current = this.pendingModifications();
      const filtered = new Map(Array.from(current.entries()).filter(([, mod]) => mod.campaignId !== campaignId));
      this.pendingModifications.set(filtered);
    }
    this.campaignMeasureStore.setHasPendingChanges(false);
  }

  private statusLabel(status: MeasureValueStatus | null | undefined): string {
    return status == null ? '' : this.translate.translate(STATUS_LABEL_KEYS[status]);
  }

  // Positional copy-all: one line per row, blank cells stay blank so a paste into
  // another campaign keeps every value aligned with its measure.
  buildCopyAllLines(): string[] {
    return this.gridRowData().map(row => (row.parameterValue == null ? '' : String(row.parameterValue)));
  }

  private processDataFromClipboard(params: ProcessDataFromClipboardParams): string[][] | null {
    // Positional paste: interior blanks clear their target cell instead of shifting the
    // values below onto the wrong measures; the cell parser blanks non-numeric input in place.
    const data = trimTrailingEmptyClipboardRow(params.data);
    return data.length > 0 ? data : null;
  }

  private readonly processCellFromClipboard = createClipboardCellParser(colId =>
    colId === 'parameterValue' ? { kind: 'numeric' } : { kind: 'passthrough' },
  );
}
